// src/scenes/ProfileDialogManager.js
// Clean DOM dialog implementation for ProfileScene - no fallbacks
import { auth } from "../firebaseConfig.js";
import { 
  initiateCompanyEmailVerification, 
  verifyCompanyEmailCode, 
  isValidEmail, 
  isPersonalEmail
} from "../services/firmAuthService.js";
import { updateDisplayName } from "../services/profileService.js";
import UIController from "../ui/uiController.js";

export default class ProfileDialogManager {
  constructor(scene) {
    this.scene = scene;
    this.companyEmailInput = null;
  }

  // Company Email Verification Dialog
  showCompanyEmailDialog() {
    console.log('🔐 showCompanyEmailDialog called');
    this.createMatrixEmailDialog();
  }

  createMatrixEmailDialog() {
    console.log('🔐 Creating Matrix email dialog');
    // Create DOM dialog using the SAME pattern as working overlays
    const overlay = document.createElement('div');
    overlay.className = 'overlay'; // Start hidden
    overlay.innerHTML = `
      <div class="overlay-content" style="max-width: 450px;">
        <h2 style="color: var(--color-primary); margin-bottom: 20px;">🔐 Company Email Verification</h2>
        
        <div style="color: var(--color-text-medium); margin-bottom: 20px; line-height: 1.6;">
          Enter your company email address to verify firm access:
        </div>
        
        <input type="email" id="matrix-email-input" placeholder="your.name@company.com" required 
               style="width: 100%; margin-bottom: 20px;" />
        
        <div style="display: flex; gap: 15px; justify-content: center;">
          <button type="button" id="matrix-email-cancel">Cancel</button>
          <button type="button" id="matrix-email-verify">Verify</button>
        </div>
      </div>
    `;

    // Add to document
    console.log('📍 Adding overlay to document body');
    document.body.appendChild(overlay);
    
    // DISABLE PHASER INPUT FIRST (same as UIController.showOverlay)
    if (UIController && typeof UIController.disableSceneInput === 'function') {
      UIController.disableSceneInput();
      console.log('📍 Phaser input disabled for email dialog');
    }
    
    // Use the SAME show pattern as working overlays
    setTimeout(() => {
      overlay.classList.add('show');
      console.log('📍 Overlay shown with .show class');
    }, 10);

    // Get elements directly
    const input = document.getElementById('matrix-email-input');
    const cancelBtn = document.getElementById('matrix-email-cancel');
    const verifyBtn = document.getElementById('matrix-email-verify');

    // Focus input
    input.focus();

    // Event handlers
    const closeDialog = () => {
      overlay.classList.remove('show');
      setTimeout(() => {
        if (overlay.parentNode) {
          document.body.removeChild(overlay);
        }
        // RE-ENABLE PHASER INPUT (same as UIController.hideOverlay)
        // Only if no other overlays are visible
        const visibleOverlays = document.querySelectorAll('.overlay.show');
        if (visibleOverlays.length === 0 && UIController && typeof UIController.enableSceneInput === 'function') {
          UIController.enableSceneInput();
          console.log('📍 Phaser input re-enabled - no overlays visible');
        }
      }, 300); // Wait for fade animation
    };

    const handleVerify = () => {
      const email = input.value.trim();
      if (email) {
        this.companyEmailInput = email;
        closeDialog();
      this.handleCompanyEmailSubmit();
      } else {
        this.scene.showMessage("Please enter an email address.", 2500, true);
      }
    };

    // Button clicks
    cancelBtn.addEventListener('click', closeDialog);
    verifyBtn.addEventListener('click', handleVerify);

    // Enter key support
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        handleVerify();
      }
    });
  }

  // Code Verification Dialog
  showCodeVerificationDialog() {
    this.createMatrixCodeDialog();
  }

  createMatrixCodeDialog() {
    // Create DOM dialog using the SAME pattern as working overlays
    const overlay = document.createElement('div');
    overlay.className = 'overlay'; // Start hidden
    overlay.innerHTML = `
      <div class="overlay-content" style="max-width: 400px;">
        <h2 style="color: var(--color-primary); margin-bottom: 20px;">🔐 Enter Verification Code</h2>
        
        <div style="color: var(--color-text-medium); margin-bottom: 20px; text-align: center;">
          Enter the 6-digit verification code from your email:
        </div>
        
        <input type="text" id="matrix-code-input" placeholder="123456" maxlength="6" 
               style="width: 120px; text-align: center; letter-spacing: 3px; font-size: 18px; margin: 0 auto 20px auto; display: block;" />
        
        <div style="display: flex; gap: 15px; justify-content: center;">
          <button type="button" id="matrix-code-cancel">Cancel</button>
          <button type="button" id="matrix-code-verify">Verify</button>
        </div>
      </div>
    `;

    // Add to document
    console.log('📍 Adding overlay to document body');
    document.body.appendChild(overlay);
    
    // DISABLE PHASER INPUT FIRST (same as UIController.showOverlay)
    if (UIController && typeof UIController.disableSceneInput === 'function') {
      UIController.disableSceneInput();
      console.log('📍 Phaser input disabled for code dialog');
    }
    
    // Use the SAME show pattern as working overlays
    setTimeout(() => {
      overlay.classList.add('show');
      console.log('📍 Overlay shown with .show class');
    }, 10);

    // Get elements directly
    const input = document.getElementById('matrix-code-input');
    const cancelBtn = document.getElementById('matrix-code-cancel');
    const verifyBtn = document.getElementById('matrix-code-verify');

    // Focus input and select all
    input.focus();
    input.select();

    // Restrict to numbers only
    input.addEventListener('input', (event) => {
      event.target.value = event.target.value.replace(/[^0-9]/g, '');
    });

    // Event handlers
    const closeDialog = () => {
      overlay.classList.remove('show');
      setTimeout(() => {
        if (overlay.parentNode) {
          document.body.removeChild(overlay);
        }
        // RE-ENABLE PHASER INPUT (same as UIController.hideOverlay)
        // Only if no other overlays are visible
        const visibleOverlays = document.querySelectorAll('.overlay.show');
        if (visibleOverlays.length === 0 && UIController && typeof UIController.enableSceneInput === 'function') {
          UIController.enableSceneInput();
          console.log('📍 Phaser input re-enabled - no overlays visible');
        }
      }, 300); // Wait for fade animation
    };

    const handleVerify = () => {
      const code = input.value.trim();
      if (code && code.length === 6) {
        closeDialog();
        this.handleCodeVerification(code);
      } else {
        this.scene.showMessage("Please enter a valid 6-digit code.", 2500, true);
        input.focus();
      }
    };

    // Button clicks
    cancelBtn.addEventListener('click', closeDialog);
    verifyBtn.addEventListener('click', handleVerify);

    // Enter key support
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        handleVerify();
      }
    });
  }

  // Identity Matrix Update Dialog
  showIdentityMatrixUpdateDialog() {
    console.log('🎯 showIdentityMatrixUpdateDialog called');
    this.createIdentityUpdateDialog();
  }

  createIdentityUpdateDialog() {
    console.log('🎯 Creating identity update dialog');
    const user = auth.currentUser;
    const currentName = this.scene.profileData?.displayName || user?.email || "N/A";

    // Create DOM dialog with DeckOps design system styling
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(0, 0, 0, 0.75);
      backdrop-filter: blur(12px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 15000;
      opacity: 0;
      transition: opacity 0.3s ease;
    `;
    
    overlay.innerHTML = `
      <div style="
        background: rgba(255, 255, 255, 0.05);
        backdrop-filter: blur(15px);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 16px;
        padding: 32px;
        max-width: 500px;
        width: 90%;
        box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
        text-align: center;
      ">
        <h2 style="
          color: #00d4ff;
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 16px;
          background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        ">🎯 Identity Matrix Update</h2>
        
        <div style="
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 24px;
          line-height: 1.5;
          font-size: 16px;
        ">
          Modify your executive identification display (3-20 characters):
        </div>
        
        <input type="text" id="matrix-name-input" placeholder="Display Name" required 
               value="${currentName}" style="
                 width: 100%;
                 margin-bottom: 24px;
                 padding: 12px 16px;
                 background: rgba(255, 255, 255, 0.1);
                 border: 1px solid rgba(255, 255, 255, 0.2);
                 border-radius: 8px;
                 color: white;
                 font-size: 16px;
                 box-sizing: border-box;
               " />
        
        <div style="display: flex; gap: 16px; justify-content: center;">
          <button type="button" id="matrix-name-cancel" style="
            background: rgba(255, 255, 255, 0.1);
            color: rgba(255, 255, 255, 0.8);
            border: 1px solid rgba(255, 255, 255, 0.2);
            padding: 12px 24px;
            border-radius: 8px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
          ">Cancel</button>
          <button type="button" id="matrix-name-update" style="
            background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 8px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            box-shadow: 0 8px 25px rgba(0, 212, 255, 0.3);
          ">Update</button>
        </div>
      </div>
    `;

    // Add to document
    console.log('📍 Adding overlay to document body');
    document.body.appendChild(overlay);
    
    // DISABLE PHASER INPUT FIRST (same as UIController.showOverlay)
    if (typeof UIController !== 'undefined' && UIController && typeof UIController.disableSceneInput === 'function') {
      UIController.disableSceneInput();
      console.log('📍 Phaser input disabled for identity dialog');
    } else {
      console.log('📍 UIController not available, continuing without input disabling');
    }
    
    // Show the overlay with animation
    setTimeout(() => {
      overlay.style.opacity = '1';
      console.log('📍 Overlay shown with fade animation');
    }, 10);

    // Get elements directly
    const input = document.getElementById('matrix-name-input');
    const cancelBtn = document.getElementById('matrix-name-cancel');
    const updateBtn = document.getElementById('matrix-name-update');

    // Focus input and select all
    input.focus();
    input.select();

    const handleUpdate = async () => {
      const newName = input.value.trim();
      if (newName && newName.length >= 3 && newName.length <= 20) {
        closeDialog();
        try {
          await updateDisplayName(user.uid, newName);
          this.scene.showMessage("Display name updated!", 1500, false);
          this.scene.scene.restart({ activeTab: "profile" });
        } catch (error) {
          console.error("Error updating display name:", error);
          this.scene.showMessage("Error updating display name.", 2500, true);
        }
      } else {
        this.scene.showMessage("Name must be 3-20 characters.", 2500, true);
        input.focus();
      }
    };

    // Event handlers
    const closeDialog = () => {
      overlay.style.opacity = '0';
      setTimeout(() => {
        if (overlay.parentNode) {
          document.body.removeChild(overlay);
        }
        // RE-ENABLE PHASER INPUT (same as UIController.hideOverlay)
        if (typeof UIController !== 'undefined' && UIController && typeof UIController.enableSceneInput === 'function') {
          UIController.enableSceneInput();
          console.log('📍 Phaser input re-enabled after dialog close');
        }
      }, 300); // Wait for fade animation
    };

    // Button clicks
    cancelBtn.addEventListener('click', closeDialog);
    updateBtn.addEventListener('click', handleUpdate);

    // Enter key support
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        handleUpdate();
      }
    });
  }

  // Success Dialog
  showSuccessDialog(title, message, onConfirm, buttonText = '✅ GOT IT') {
    this.createMatrixSuccessDialog(title, message, onConfirm, buttonText);
  }

  createMatrixSuccessDialog(title, message, onConfirm, buttonText) {
    // Create DOM dialog using the SAME pattern as working overlays
    const overlay = document.createElement('div');
    overlay.className = 'overlay'; // Start hidden
    overlay.innerHTML = `
      <div class="overlay-content" style="max-width: 500px;">
        <h2 style="color: var(--color-primary); margin-bottom: 20px;">${title}</h2>
        
        <div style="color: var(--color-text-light); margin-bottom: 25px; line-height: 1.6; white-space: pre-line;">
          ${message}
        </div>
        
        <div style="display: flex; justify-content: center;">
          <button type="button" id="matrix-success-ok">${buttonText}</button>
        </div>
      </div>
    `;

    // Add to document
    console.log('📍 Adding overlay to document body');
    document.body.appendChild(overlay);
    
    // DISABLE PHASER INPUT FIRST (same as UIController.showOverlay)
    if (UIController && typeof UIController.disableSceneInput === 'function') {
      UIController.disableSceneInput();
      console.log('📍 Phaser input disabled for success dialog');
    }
    
    // Use the SAME show pattern as working overlays
    setTimeout(() => {
      overlay.classList.add('show');
      console.log('📍 Overlay shown with .show class');
    }, 10);

    // Get elements directly
    const okBtn = document.getElementById('matrix-success-ok');

    // Event handlers
    const closeDialog = () => {
      overlay.classList.remove('show');
      setTimeout(() => {
        if (overlay.parentNode) {
          document.body.removeChild(overlay);
        }
        // RE-ENABLE PHASER INPUT (same as UIController.hideOverlay)
        // Only if no other overlays are visible
        const visibleOverlays = document.querySelectorAll('.overlay.show');
        if (visibleOverlays.length === 0 && UIController && typeof UIController.enableSceneInput === 'function') {
          UIController.enableSceneInput();
          console.log('📍 Phaser input re-enabled - no overlays visible');
        }
        if (onConfirm) {
          onConfirm();
        }
      }, 300); // Wait for fade animation
    };

    // Button click
    okBtn.addEventListener('click', closeDialog);

    // Enter key support
    document.addEventListener('keydown', function enterHandler(event) {
      if (event.key === 'Enter') {
        document.removeEventListener('keydown', enterHandler);
        closeDialog();
      }
    });
  }



  // Email submission handler
  async handleCompanyEmailSubmit(dialog = null) {
    if (dialog) dialog.destroy();
    
    const email = this.companyEmailInput;
    if (!email || !isValidEmail(email)) {
      this.scene.showMessage("Please enter a valid email address.", 2500, true);
      return;
    }

    if (isPersonalEmail(email)) {
      this.scene.showMessage("Please use a company email, not personal email.", 2500, true);
      return;
    }

    // Show loading message
    this.scene.showMessage("🔄 Processing company email verification...", 2000, false);
    
    try {
      const result = await initiateCompanyEmailVerification(email);
      
      if (result.success) {
        // Check if this is code-based verification
        if (result.message.includes("6-digit code")) {
          // Code-based verification - show success dialog with code input option
          this.showSuccessDialog(
            "✅ Verification Code Sent!",
            `📧 A 6-digit verification code has been sent to:\n${email}\n\n🔍 Check your email inbox (and spam folder) for the code.\n\n⏱️ The email should arrive within 2-3 minutes.\n\n🔢 Click "ENTER CODE" below when you receive it.`,
            () => {
              // Show code verification dialog
              this.showCodeVerificationDialog();
            },
            "🔢 ENTER CODE" // Custom button text
          );
        } else {
          // Link-based verification - show original success dialog
          this.showSuccessDialog(
            "✅ Verification Email Sent Successfully!",
            `📧 A verification email has been sent to:\n${email}\n\n🔍 Please check your email inbox (and spam folder) for the verification link.\n\n⏱️ The email should arrive within 2-5 minutes.\n\n🔐 Click the green "VERIFY COMPANY EMAIL" button in the email to complete verification.`,
            () => {
              // Restart scene after user acknowledges
              this.scene.scene.restart({ activeTab: "profile" });
            }
          );
        }
      } else {
        this.scene.showMessage(`❌ ${result.message}`, 4000, true);
      }
    } catch (error) {
      console.error("Error submitting company email:", error);
      this.scene.showMessage("❌ Failed to submit verification request. Please try again.", 4000, true);
    }
  }

  // Code verification handler
  async handleCodeVerification(code) {
    if (!code || code.length !== 6) {
      this.scene.showMessage("Please enter a valid 6-digit code.", 2500, true);
      return;
    }

    // Show loading message
    this.scene.showMessage("🔄 Verifying code...", 2000, false);
    
    try {
      const result = await verifyCompanyEmailCode(code);
      
      if (result.success) {
        this.showSuccessDialog(
          "🎉 Email Verified Successfully!",
          `✅ Your company email has been verified!\n\n🎯 You now have access to firm-specific features.\n\n🚀 Restart the scene to see your new access level.`,
          () => {
            // Restart scene to show updated firm access
            this.scene.scene.restart({ activeTab: "profile" });
          }
        );
      } else {
        this.scene.showMessage(`❌ ${result.message}`, 4000, true);
        // Offer to try again
        setTimeout(() => {
          this.showCodeVerificationDialog();
        }, 2000);
      }
    } catch (error) {
      console.error("Error verifying code:", error);
      this.scene.showMessage("❌ Failed to verify code. Please try again.", 4000, true);
    }
  }


}